import {FlowNodeStyles, FlowNodeTypes} from "@/types/node";
import {capitalize} from "@/utils/index";
import {
  PhoneOff,
  Zap,
  AlignLeft,
  Workflow,
  Settings,
  Phone,
  MessageCircle,
  RedoDot,
  Calendar,
  ArrowLeftRight,
} from "lucide-react";
import {
  startNodeBlueprint,
  actionNodeBlueprint,
  endNodeBlueprint,
  stepNodeBlueprint,
  jumpNodeBlueprint,
  branchNodeBlueprint,
  globalNodeBlueprint,
  collectVariablesNodeBlueprint,
  speakNodeBlueprint,
  transferNodeBlueprint,
  bookingNodeBlueprint,
  entryNodeBlueprint,
} from "./Blueprints";
import {getTailwindColors} from "@/constants/tailwindConfigs";
import {
  StartNodeIcon,
  GlobalNodeIcon,
  JumpNodeIcon,
  EndNodeIcon,
  SpeakNodeIcon,
  CollectVariablesNodeIcon,
  ActionNodeIcon,
  TransferNodeIcon,
  BookingNodeIcon,
  EntryNodeIcon,
} from "@/components/icons/NodeTypeIcons";
import {CaptureVariableIcon} from "@/components/icons/CaptureVariableIcon";
import {Entry} from "@/components/icons";

const TailwindColors = getTailwindColors();

const nodeStyles = {
  [FlowNodeTypes.ROOT]: {},
  [FlowNodeTypes.GLOBAL]: {
    iconColor: TailwindColors.slate["800"],
    iconBackgroundColor: TailwindColors.muted["DEFAULT"],
    iconBorderColor: "var(--fb-border)",
    minimapNodeColor: TailwindColors.gray["100"],
    miniMapNodeSelectedColor: TailwindColors.gray["200"],
    contextColor: TailwindColors.gray["100"],
    backgroundColor: TailwindColors.slate["600"],
    backgroundColorActive: TailwindColors.slate["800"],
    borderColor: TailwindColors.slate["600"],
    borderColorHover: TailwindColors.slate["800"],
    borderColorActive: TailwindColors.slate["300"],
    inverted: true,
  },
  [FlowNodeTypes.START]: {
    iconColor: TailwindColors.green["DEFAULT"],
    iconBackgroundColor: TailwindColors.green["DEFAULT"],
    iconBorderColor: TailwindColors.green["100"],
    minimapNodeColor: TailwindColors.green["100"],
    miniMapNodeSelectedColor: TailwindColors.green["200"],
    contextColor: TailwindColors.green["50"],
    backgroundColor: TailwindColors.green["100"],
    borderColor: TailwindColors.green["100"],
    borderColorHover: TailwindColors.green["200"],
    borderColorActive: TailwindColors.green["16"],
  },
  [FlowNodeTypes.STEP]: {
    iconColor: TailwindColors.primary["DEFAULT"],
    iconBackgroundColor: TailwindColors.violet["50"],
    iconBorderColor: TailwindColors.violet["100"],
    minimapNodeColor: TailwindColors.violet["100"],
    miniMapNodeSelectedColor: TailwindColors.violet["200"],
    contextColor: TailwindColors.violet["50"],
    backgroundColor: TailwindColors.violet["100"],
    borderColor: TailwindColors.violet["100"],
    borderColorHover: TailwindColors.violet["200"],
    borderColorActive: TailwindColors.violet["16"],
  },
  [FlowNodeTypes.BRANCH]: {
    iconColor: TailwindColors.primary["DEFAULT"],
    iconBackgroundColor: TailwindColors.violet["DEFAULT"],
    iconBorderColor: TailwindColors.violet["100"],
    minimapNodeColor: TailwindColors.violet["100"],
    miniMapNodeSelectedColor: TailwindColors.violet["200"],
    contextColor: TailwindColors.violet["200"],
    backgroundColor: TailwindColors.violet["200"],
    backgroundColorActive: TailwindColors.violet["300"],
    borderColor: TailwindColors.violet["100"],
    borderColorHover: TailwindColors.violet["200"],
    borderColorActive: TailwindColors.violet["100"],
  },
  [FlowNodeTypes.JUMP]: {
    iconColor: TailwindColors.azure["DEFAULT"],
    iconBackgroundColor: TailwindColors.azure["DEFAULT"],
    iconBorderColor: TailwindColors.azure["100"],
    minimapNodeColor: TailwindColors.azure["100"],
    miniMapNodeSelectedColor: TailwindColors.azure["200"],
    contextColor: TailwindColors.azure["50"],
    backgroundColor: TailwindColors.azure.lighter,
    borderColor: TailwindColors.azure["100"],
    borderColorHover: TailwindColors.azure["200"],
    borderColorActive: TailwindColors.azure["16"],
  },
  [FlowNodeTypes.END]: {
    iconColor: TailwindColors.red["DEFAULT"],
    iconBackgroundColor: TailwindColors.red["DEFAULT"],
    iconBorderColor: TailwindColors.red["100"],
    minimapNodeColor: TailwindColors.red["100"],
    miniMapNodeSelectedColor: TailwindColors.red["200"],
    contextColor: TailwindColors.red["50"],
    backgroundColor: TailwindColors.red.lighter,
    borderColor: TailwindColors.red["100"],
    borderColorHover: TailwindColors.red["200"],
    borderColorActive: TailwindColors.red["16"],
  },
  [FlowNodeTypes.PLACEHOLDER]: {
    iconClassName: "",
    iconWrapperClassName: "",
    minimapNodeColor: TailwindColors.transparent,
    miniMapNodeSelectedColor: TailwindColors.transparent,
    contextColor: TailwindColors.transparent,
  },
  [FlowNodeTypes.COLLECT_VARIABLES]: {
    iconColor: TailwindColors.primary["DEFAULT"],
    iconBackgroundColor: TailwindColors.violet["DEFAULT"],
    iconBorderColor: TailwindColors.violet["100"],
    minimapNodeColor: TailwindColors.violet["100"],
    miniMapNodeSelectedColor: TailwindColors.violet["200"],
    contextColor: TailwindColors.violet["50"],
    backgroundColor: TailwindColors.violet.lighter,
    borderColor: TailwindColors.violet["100"],
    borderColorHover: TailwindColors.violet["200"],
    borderColorActive: TailwindColors.violet["16"],
  },
  [FlowNodeTypes.SPEAK]: {
    iconColor: TailwindColors.primary["DEFAULT"],
    iconBackgroundColor: TailwindColors.violet["DEFAULT"],
    iconBorderColor: TailwindColors.violet["100"],
    minimapNodeColor: TailwindColors.violet["100"],
    miniMapNodeSelectedColor: TailwindColors.violet["200"],
    contextColor: TailwindColors.violet["50"],
    backgroundColor: TailwindColors.violet.lighter,
    borderColor: TailwindColors.violet["100"],
    borderColorHover: TailwindColors.violet["200"],
    borderColorActive: TailwindColors.violet["16"],
  },
  [FlowNodeTypes.ACTION]: {
    iconColor: TailwindColors.blue["DEFAULT"],
    iconBackgroundColor: TailwindColors.blue["DEFAULT"],
    iconBorderColor: TailwindColors.blue["100"],
    minimapNodeColor: TailwindColors.blue["100"],
    miniMapNodeSelectedColor: TailwindColors.blue["200"],
    contextColor: TailwindColors.blue["50"],
    backgroundColor: TailwindColors.blue.lighter,
    borderColor: TailwindColors.blue["100"],
    borderColorHover: TailwindColors.blue["200"],
    borderColorActive: TailwindColors.blue["16"],
  },
  [FlowNodeTypes.TRANSFER]: {
    iconColor: TailwindColors.yellow["DEFAULT"],
    iconBackgroundColor: TailwindColors.yellow["DEFAULT"],
    iconBorderColor: TailwindColors.yellow["100"],
    minimapNodeColor: TailwindColors.yellow["100"],
    miniMapNodeSelectedColor: TailwindColors.yellow["200"],
    contextColor: TailwindColors.yellow["50"],
    backgroundColor: TailwindColors.yellow.lighter,
    borderColor: TailwindColors.yellow["100"],
    borderColorHover: TailwindColors.yellow["200"],
    borderColorActive: TailwindColors.yellow["16"],
  },
  [FlowNodeTypes.BOOKING]: {
    iconColor: TailwindColors.orange["DEFAULT"],
    iconBackgroundColor: TailwindColors.orange["DEFAULT"],
    iconBorderColor: TailwindColors.orange["100"],
    minimapNodeColor: TailwindColors.orange["100"],
    miniMapNodeSelectedColor: TailwindColors.orange["200"],
    contextColor: TailwindColors.orange["50"],
    backgroundColor: TailwindColors.orange.lighter,
    borderColor: TailwindColors.orange["100"],
    borderColorHover: TailwindColors.orange["200"],
    borderColorActive: TailwindColors.orange["16"],
  },
  [FlowNodeTypes.ENTRY]: {
    iconColor: TailwindColors.slate["800"],
    iconBackgroundColor: TailwindColors.muted["DEFAULT"],
    iconBorderColor: "var(--fb-border)",
    minimapNodeColor: TailwindColors.gray["100"],
    miniMapNodeSelectedColor: TailwindColors.gray["200"],
    contextColor: TailwindColors.gray["100"],
    backgroundColor: TailwindColors.slate["600"],
    backgroundColorActive: TailwindColors.slate["800"],
    borderColor: TailwindColors.slate["600"],
    borderColorHover: TailwindColors.slate["800"],
    borderColorActive: TailwindColors.slate["300"],
    inverted: true,
  },
};

export const FlowNodesData: Record<FlowNodeTypes, FlowNodeStyles> = {
  [FlowNodeTypes.ROOT]: {
    name: capitalize(FlowNodeTypes.ROOT),
    icon: null,
    blueprint: null,
    styles: nodeStyles[FlowNodeTypes.ROOT],
  },
  [FlowNodeTypes.GLOBAL]: {
    name: capitalize(FlowNodeTypes.GLOBAL),
    icon: Settings,
    iconInverted: GlobalNodeIcon,
    blueprint: globalNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.GLOBAL],
  },
  [FlowNodeTypes.START]: {
    name: capitalize(FlowNodeTypes.START),
    icon: Phone,
    iconInverted: StartNodeIcon,
    blueprint: startNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.START],
  },
  [FlowNodeTypes.COLLECT_VARIABLES]: {
    name: capitalize(collectVariablesNodeBlueprint.data.nodeTitle),
    icon: CaptureVariableIcon,
    iconInverted: CollectVariablesNodeIcon,
    blueprint: collectVariablesNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.COLLECT_VARIABLES],
  },
  [FlowNodeTypes.SPEAK]: {
    name: capitalize(FlowNodeTypes.SPEAK),
    icon: MessageCircle,
    iconInverted: SpeakNodeIcon,
    blueprint: speakNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.SPEAK],
  },
  [FlowNodeTypes.BRANCH]: {
    name: capitalize(FlowNodeTypes.BRANCH),
    icon: Workflow,
    blueprint: branchNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.BRANCH],
  },
  [FlowNodeTypes.JUMP]: {
    name: capitalize(FlowNodeTypes.JUMP),
    icon: RedoDot,
    iconInverted: JumpNodeIcon,
    blueprint: jumpNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.JUMP],
  },
  [FlowNodeTypes.END]: {
    name: capitalize(FlowNodeTypes.END),
    icon: PhoneOff,
    iconInverted: EndNodeIcon,
    blueprint: endNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.END],
  },
  [FlowNodeTypes.PLACEHOLDER]: {
    name: capitalize(FlowNodeTypes.PLACEHOLDER),
    icon: null,
    blueprint: null,
    styles: nodeStyles[FlowNodeTypes.PLACEHOLDER],
  },
  [FlowNodeTypes.STEP]: {
    name: capitalize(FlowNodeTypes.STEP),
    icon: AlignLeft,
    blueprint: stepNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.STEP],
  },
  [FlowNodeTypes.ACTION]: {
    name: capitalize(FlowNodeTypes.ACTION),
    icon: Zap,
    iconInverted: ActionNodeIcon,
    blueprint: actionNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.ACTION],
  },
  [FlowNodeTypes.TRANSFER]: {
    name: capitalize(FlowNodeTypes.TRANSFER),
    icon: ArrowLeftRight,
    iconInverted: TransferNodeIcon,
    blueprint: transferNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.TRANSFER],
  },
  [FlowNodeTypes.BOOKING]: {
    name: capitalize(FlowNodeTypes.BOOKING),
    icon: Calendar,
    iconInverted: BookingNodeIcon,
    blueprint: bookingNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.BOOKING],
  },
  [FlowNodeTypes.ENTRY]: {
    name: capitalize(FlowNodeTypes.ENTRY),
    icon: EntryNodeIcon,
    iconInverted: Entry,
    blueprint: entryNodeBlueprint,
    styles: nodeStyles[FlowNodeTypes.ENTRY],
  },
  [FlowNodeTypes.INSTRUCTION]: null,
};
